import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  fetchUsers,
  updateUserStatus,
  deleteUser,
  changeUserPassword,
  sendMessageToUser,
  restoreUser,
  permanentDeleteUser,
} from '../../../store/slices/adminSlice';
import { useAdminPermissions } from '../../../hooks/useAdminPermissions';
import toast from 'react-hot-toast';
import UniversalConfirmModal from '../../../components/UniversalConfirmModal/UniversalConfirmModal';
import ChangePasswordModal from '../../../components/ChangePasswordModal/ChangePasswordModal';
import SendMessageModal from '../../../components/SendMessageModal/SendMessageModal';
import { formatStatusForDisplay } from '../../../utils/statusFormatter';
import { renderSortIcon } from '../../../utils/sortIconHelper';
import adminService from '../../../services/adminService';

const AirlinerManagement = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { users, pagination, loading, statusUpdateLoading, deleteLoading } = useAppSelector((state) => state.admin);
  const { perms } = useAdminPermissions();
  const p = perms.airliners;
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [userTypeFilter, setUserTypeFilter] = useState('Airliner');
  const [statusFilter, setStatusFilter] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [showAddUserMenu, setShowAddUserMenu] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSendMessageModal, setShowSendMessageModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    userId: true,
    firstName: true,
    lastName: true,
    email: true,
    mobile: true,
    userType: true,
    status: true,
    created: true
  });
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Fetch users on component mount and when filters change
  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, debouncedSearchTerm, userTypeFilter, statusFilter, sortBy, sortOrder]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadUsers = () => {
    dispatch(fetchUsers({
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearchTerm || '',
      user_type: userTypeFilter,
      status: statusFilter,
      sort_by: sortBy,
      sort_order: sortOrder
    }));
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => (prev === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortBy(column);
      setSortOrder("ASC");
    }
  };

  const getSortIcon = (column) => {
    return renderSortIcon(column, sortBy, sortOrder);
  };

  const handleViewUser = (user) => {
    navigate(`/admin/airliners/${user.id}`, { state: { returnTo: 'airliners', type: 'Airliner' } });
  };

  const handleAircraftManagement = (user) => {
    navigate(`/admin/airliners/${user.id}/aircrafts`);
  };

  const handleFlightManagement = (user) => {
    navigate(`/admin/airliners/${user.id}/flights`);
  };

  const handleEditUser = (user) => {
    navigate(`/admin/users/${user.id}/edit`);
  };

  const handleEmailUser = (user) => {
    setSelectedUser(user);
    setShowSendMessageModal(true);
  };

  const handleChangePassword = (user) => {
    setSelectedUserId(user.id);
    setShowPasswordModal(true);
  };

  const handleChangeStatus = (user) => {
    setSelectedUser(user);
    setConfirmAction(user.status === 'Active' ? 'disable' : 'enable');
    setShowConfirmModal(true);
  };

  const handleConfirmAction = () => {
    if (!selectedUser) return;
    if (confirmAction === 'delete') {
      dispatch(deleteUser(selectedUser.id))
        .unwrap()
        .then(() => {
          toast.success('User deleted successfully');
          loadUsers();
        })
        .catch((error) => {
          toast.error(error || 'Failed to delete user');
        });
    } else if (confirmAction === 'restore') {
      dispatch(restoreUser(selectedUser.id))
        .unwrap()
        .then(() => {
          toast.success('User restored successfully');
          loadUsers();
        })
        .catch((error) => {
          toast.error(error || 'Failed to restore user');
        });
    } else if (confirmAction === 'permanentDelete') {
      dispatch(permanentDeleteUser(selectedUser.id))
        .unwrap()
        .then(() => {
          toast.success('User permanently deleted successfully');
          loadUsers();
        })
        .catch((error) => {
          toast.error(error || 'Failed to permanently delete user');
        });
    } else {
      const newStatus = selectedUser.status === 'Active' ? 'Deactive' : 'Active';

      dispatch(updateUserStatus({ id: selectedUser.id, status: newStatus }))
        .unwrap()
        .then(() => {
          toast.success(`User status changed to ${newStatus}`);
        })
        .catch((error) => {
          toast.error(error || 'Failed to update status');
        });
    }
    setShowConfirmModal(false);
    setSelectedUser(null);
    setConfirmAction(null);
  };

  const handleCancelModal = () => {
    setShowConfirmModal(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) {
      toast.error('User not found');
      return;
    }
    setSelectedUser(user);
    setConfirmAction('delete');
    setShowConfirmModal(true);
  };

  const handleRestoreUser = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) {
      toast.error('User not found');
      return;
    }
    setSelectedUser(user);
    setConfirmAction('restore');
    setShowConfirmModal(true);
  };

  const handlePermanentDeleteUser = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) {
      toast.error('User not found');
      return;
    }
    setSelectedUser(user);
    setConfirmAction('permanentDelete');
    setShowConfirmModal(true);
  };

  const getUserTypeBadgeClass = (userType) => {
    switch (userType) {
      case 'JetSetter': return 'bg-[#00c0ef] text-white';
      case 'Airliner': return 'bg-[#f39c12] text-white';
      case 'SubAdmin': return 'bg-[#00a65a] text-white';
      case 'Admin': return 'bg-[#00c0ef] text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getStatusBadgeClass = (status) => {
    return status === 'Active' ? 'bg-[#00c0ef] text-white' : 'bg-[#f39c12] text-white';
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page
  };

  const exportToExcel = async (e) => {
    e.preventDefault();
    try {
      toast.loading('Preparing Excel export...', { id: 'export' });
      const blob = await adminService.exportUsersExcel({ user_type: 'Airliner', search: debouncedSearchTerm });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'airliner_management.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported successfully', { id: 'export' });
    } catch (error) {
      toast.error('Failed to export Excel', { id: 'export' });
    }
    setShowExportMenu(false);
  };

  const exportToPDF = async (e) => {
    e.preventDefault();
    try {
      toast.loading('Preparing PDF export...', { id: 'export' });
      const response = await adminService.exportUsersPDF({ user_type: 'Airliner', search: debouncedSearchTerm });
      if (!response?.data || response.data.length === 0) {
        toast.error('No data to export', { id: 'export' });
        return;
      }
      const users = response.data;
      const doc = new jsPDF('landscape');
      const tableColumn = ['#', 'User ID', 'First Name', 'Last Name', 'Email', 'Mobile', 'User Type', 'Status', 'Created At'];
      const tableRows = users.map(u => [u.index, u.user_id, u.first_name, u.last_name, u.email, u.mobile_number, u.user_type, u.status, u.created_at]);
      doc.text(`Airliner Management Export (${response.total} records)`, 14, 15);
      autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20, styles: { fontSize: 7 }, headStyles: { fillColor: [3, 83, 143] } });
      doc.save('airliner_management.pdf');
      toast.success('PDF exported successfully', { id: 'export' });
    } catch (error) {
      toast.error('Failed to export PDF', { id: 'export' });
    }
    setShowExportMenu(false);
  };

  if (loading && users.length === 0) {
    return <div className="p-12 text-center">Loading...</div>;
  }

  return (
    <div className="p-4">
      {/* Page Header - Outside white container */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800 m-0">Airliner Management</h1>

        <div className="flex items-center gap-2">
          {/* Flight Charges Button */}
          <button
            onClick={() => navigate('/admin/flight-charges')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-black bg-gradient-to-br from-[#ffeb00] to-[#ffd200] rounded hover:opacity-90 transition-opacity"
            title="Manage Flight Charges"
          >
            <i className="fas fa-plane"></i>
            <span>Flight Management</span>
          </button>

          {/* Export Button */}
          {p.export && (
            <div className="relative">
              <button
                onMouseEnter={() => setShowExportMenu(true)}
                onMouseLeave={() => setShowExportMenu(false)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-black bg-gradient-to-br from-[#ffeb00] to-[#ffd200] rounded hover:opacity-90 transition-opacity"
              >
                <i className="fas fa-file-export"></i>
                <span>Export</span>
              </button>
              {showExportMenu && (
                <div
                  onMouseEnter={() => setShowExportMenu(true)}
                  onMouseLeave={() => setShowExportMenu(false)}
                  className="absolute top-full left-0 mt-1 w-40 bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border border-gray-200 rounded shadow-lg z-50"
                >
                  <a href="#" onClick={exportToExcel} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 no-underline">
                    <i className="fas fa-file-excel text-black-600"></i>
                    <span>Excel</span>
                  </a>
                  <a href="#" onClick={exportToPDF} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 no-underline">
                    <i className="fas fa-file-pdf text-black-600"></i>
                    <span>PDF</span>
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Hide/Show Column Button */}
          <div className="relative">
            <button
              onMouseEnter={() => setShowColumnMenu(true)}
              onMouseLeave={() => setShowColumnMenu(false)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-black bg-gradient-to-br from-[#ffeb00] to-[#ffd200] rounded hover:opacity-90 transition-opacity"
            >
              <span>Hide/Show Column</span>
              <i className="fas fa-caret-down"></i>
            </button>
            {showColumnMenu && (
              <div
                onMouseEnter={() => setShowColumnMenu(true)}
                onMouseLeave={() => setShowColumnMenu(false)}
                className="absolute top-full right-0 mt-1 w-48 bg-white border border-gray-200 rounded shadow-lg z-50 p-3"
              >
                {Object.entries(visibleColumns).map(([key, value]) => (
                  <label key={key} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 px-2 rounded">
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={() => setVisibleColumns({ ...visibleColumns, [key]: !value })}
                      className="w-4 h-4 accent-[#ffd200]"
                    />
                    <span className="text-sm text-gray-700">{key.replace(/([A-Z])/g, ' $1').trim().charAt(0).toUpperCase() + key.replace(/([A-Z])/g, ' $1').trim().slice(1)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Add Airliner Button */}
          {p.add && (
            <button
              onClick={() => navigate('/admin/airliners/create')}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-black bg-gradient-to-br from-[#ffeb00] to-[#ffd200] rounded hover:opacity-90 transition-opacity"
            >
              <i className="fas fa-plus"></i>
              <span>Add Airliner</span>
            </button>
          )}
        </div>
      </div>

      {/* White container with table */}
      <div className="bg-white rounded shadow-sm">
        {/* Pagination at top */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Display</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#ffd200]"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-600">Records</span>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Search:</span>
              <input
                type="search"
                name="search_term"
                autoComplete="off"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#ffd200]"
                placeholder="Search users..."
              />
            </div>

            <div className="flex items-center">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              {[...Array(Math.min(pagination.total_pages, 9))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    disabled={loading}
                    className={`px-3 py-1.5 text-sm border border-gray-300 transition-colors ${currentPage === pageNum
                      ? 'bg-[#ffd200] text-black border-[#ffd200]'
                      : 'bg-white hover:bg-gray-50'
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.total_pages || loading}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100 border-b border-gray-300">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300">#</th>
                {visibleColumns.userId && <th onClick={() => handleSort("user_id")} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">User ID {getSortIcon("user_id")}</th>}
                {visibleColumns.firstName && <th onClick={() => handleSort("first_name")} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">First Name {getSortIcon("first_name")}</th>}
                {visibleColumns.lastName && <th onClick={() => handleSort("last_name")} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">Last Name {getSortIcon("last_name")}</th>}
                {visibleColumns.email && <th onClick={() => handleSort("email")} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">Email {getSortIcon("email")}</th>}
                {visibleColumns.mobile && <th onClick={() => handleSort("mobile_number")} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">Mobile Number {getSortIcon("mobile_number")}</th>}
                {visibleColumns.userType && <th onClick={() => handleSort("user_type")} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">User Type {getSortIcon("user_type")}</th>}
                {visibleColumns.status && <th onClick={() => handleSort("status")} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">Status {getSortIcon("status")}</th>}
                {visibleColumns.created && <th onClick={() => handleSort("created_at")} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">Date/Time Created {getSortIcon("created_at")}</th>}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Loading users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    {visibleColumns.userId && <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{user.user_id || '-'}</td>}
                    {visibleColumns.firstName && <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{user.first_name || '-'}</td>}
                    {visibleColumns.lastName && <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{user.last_name || '-'}</td>}
                    {visibleColumns.email && <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{user.email}</td>}
                    {visibleColumns.mobile && <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{user.mobile_number || '-'}</td>}
                    {visibleColumns.userType && (
                      <td className="px-4 py-3 text-sm border-r border-gray-200">
                        <span className={`inline-block px-3 py-1 text-xs font-semibold rounded ${getUserTypeBadgeClass(user.user_type)}`}>
                          {user.user_type || 'N/A'}
                        </span>
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td className="px-4 py-3 text-sm border-r border-gray-200">
                        <span className={`inline-block px-3 py-1 text-xs font-semibold rounded ${getStatusBadgeClass(user.status)}`}>
                          {formatStatusForDisplay(user.status)?.toUpperCase() || 'N/A'}
                        </span>
                      </td>
                    )}
                    {visibleColumns.created && (
                      <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">
                        {user.created_at ? new Date(user.created_at).toLocaleString() : '-'}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {user.deleted_at ? (
                          <>
                            {p.archive && (
                              <button
                                onClick={() => handleRestoreUser(user.id)}
                                className="relative text-left whitespace-nowrap overflow-hidden text-ellipsis h-6 w-6 p-0 flex items-center justify-center rounded bg-[#3b5998] text-white hover:bg-[#2d4373] transition-colors"
                                title="Restore Airliner"
                              >
                                <i className="fas fa-trash-restore text-xs"></i>
                              </button>
                            )}
                            {p.delete && (
                              <button
                                onClick={() => handlePermanentDeleteUser(user.id)}
                                disabled={deleteLoading}
                                className="relative text-left whitespace-nowrap overflow-hidden text-ellipsis h-6 w-6 p-0 flex items-center justify-center rounded bg-[#dd4b39] text-white hover:bg-[#c0392b] transition-colors"
                                title="Permanently Delete"
                              >
                                <i className="fas fa-trash text-xs"></i>
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            {/* Aircraft & Flight Management — visible with view permission */}
                            {p.view && (
                              <button
                                onClick={() => handleAircraftManagement(user)}
                                className="relative text-left whitespace-nowrap overflow-hidden text-ellipsis h-6 w-6 p-0 flex items-center justify-center rounded bg-[#03538f] text-white hover:bg-[#2d4373] transition-colors"
                                title="Aircraft Management"
                              >
                                <i className="fas fa-plane-departure text-xs"></i>
                              </button>
                            )}
                            {p.view && (
                              <button
                                onClick={() => handleFlightManagement(user)}
                                className="relative text-left whitespace-nowrap overflow-hidden text-ellipsis h-6 w-6 p-0 flex items-center justify-center rounded bg-[#03538f] text-white hover:bg-[#2d4373] transition-colors"
                                title="Flight Management"
                              >
                                <i className="fas fa-plane text-xs"></i>
                              </button>
                            )}
                            {p.edit && (
                              <button
                                onClick={() => handleChangeStatus(user)}
                                disabled={statusUpdateLoading}
                                className="relative text-left whitespace-nowrap overflow-hidden text-ellipsis h-6 w-6 p-0 flex items-center justify-center rounded bg-[#03538f] text-white hover:bg-[#2d4373] transition-colors"
                                title={`Change Status (Currently ${user.status})`}
                              >
                                <i className={`fas ${user.status === 'Active' ? 'fa-user-lock' : 'fa-user-tie'} text-xs`}></i>
                              </button>
                            )}
                            {p.view && (
                              <button
                                onClick={() => handleViewUser(user)}
                                className="relative text-left whitespace-nowrap overflow-hidden text-ellipsis h-6 w-6 p-0 flex items-center justify-center rounded bg-[#03538f] text-white hover:bg-[#2d4373] transition-colors"
                                title="View Details"
                              >
                                <i className="fas fa-eye text-xs"></i>
                              </button>
                            )}
                            {p.edit && (
                              <button
                                onClick={() => handleChangePassword(user)}
                                className="relative text-left whitespace-nowrap overflow-hidden text-ellipsis h-6 w-6 p-0 flex items-center justify-center rounded bg-[#03538f] text-white hover:bg-[#2d4373] transition-colors"
                                title="Change Password"
                              >
                                <i className="fa fa-key text-xs"></i>
                              </button>
                            )}
                            {p.edit && (user.user_type === 'JetSetter' || user.user_type === 'Airliner' || user.user_type === 'SubAdmin') && (
                              <button
                                onClick={() => handleEmailUser(user)}
                                className="relative text-left whitespace-nowrap overflow-hidden text-ellipsis h-6 w-6 p-0 flex items-center justify-center rounded bg-[#ffd200] text-black hover:opacity-80 transition-opacity"
                                title="Send Message"
                              >
                                <i className="fas fa-comment text-xs"></i>
                              </button>
                            )}
                            {p.edit && (
                              <button
                                onClick={() => handleEditUser(user)}
                                className="relative text-left whitespace-nowrap overflow-hidden text-ellipsis h-6 w-6 p-0 flex items-center justify-center rounded bg-[#ffd200] text-black hover:opacity-80 transition-opacity"
                                title="Edit"
                              >
                                <i className="fa fa-pencil text-xs"></i>
                              </button>
                            )}
                            {p.archive && (
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                disabled={deleteLoading}
                                className="relative text-left whitespace-nowrap overflow-hidden text-ellipsis h-6 w-6 p-0 flex items-center justify-center rounded bg-[#dd4b39] text-white hover:bg-[#c23321] transition-colors disabled:opacity-50"
                                title="Archive Airliner"
                              >
                                <i className="fas fa-archive text-xs"></i>
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer - Showing entries info */}
        <div className="p-4 bg-gray-50 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {users.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, pagination.total)} of {pagination.total} entries
          </div>
        </div>
      </div>

      {/* Modals */}
      <UniversalConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmAction}
        onClose={handleCancelModal}
        type="error"
        title="Are you sure?"
        message={
          confirmAction === "delete"
            ? 'You are about to DELETE this user account. Click "DELETE" to proceed or "CANCEL" to exit.'
            : confirmAction === "restore"
              ? 'You are about to RESTORE this user account. Click "RESTORE" to proceed or "CANCEL" to exit.'
              : confirmAction === "permanentDelete"
                ? 'You are about to PERMANENTLY DELETE this user account. This action cannot be undone. Click "PERMANENTLY DELETE" to proceed or "CANCEL" to exit.'
                : confirmAction === "disable"
                  ? 'You are about to DISABLE the account access for this User. Click "YES" to proceed or "CANCEL" to exit.'
                  : 'You are about to ENABLE the account access for this User. Click "YES" to proceed or "CANCEL" to exit.'
        }
        confirmText={
          confirmAction === "delete"
            ? "DELETE"
            : confirmAction === "restore"
              ? "RESTORE"
              : confirmAction === "permanentDelete"
                ? "PERMANENTLY DELETE"
                : "YES"
        }
        cancelText="CANCEL"
        theme="modern"
      />
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        mode="admin"
        userId={selectedUserId}
      />

      <SendMessageModal
        isOpen={showSendMessageModal}
        onClose={() => setShowSendMessageModal(false)}
        user={selectedUser}
      />
    </div>
  );
};

export default AirlinerManagement;



